export const PLATFORM_SLUG = 'platform' as const;
export type PlatformSlug = typeof PLATFORM_SLUG;

export const INSIGHTS_SLUG = 'insights' as const;
export type InsightsSlug = typeof INSIGHTS_SLUG;

export const EPML_SLUG = 'epml' as const;
export type EpmlSlug = typeof EPML_SLUG;

export const PMC_SLUG = 'pmc' as const;
export type PmcSlug = typeof PMC_SLUG;

export const PASSWORD_SAFE_SLUG = 'passwordsafe' as const;
export type PasswordSafeSlug = typeof PASSWORD_SAFE_SLUG;

export const SECRETS_SLUG = 'secrets' as const;
export type SecretsSlug = typeof SECRETS_SLUG;

export const ENTITLE_SLUG = 'entitle' as const;
export type EntitleSlug = typeof ENTITLE_SLUG;

export const REMOTE_SUPPORT_SLUG = 'rs' as const;
export type RemoteSupportSlug = typeof REMOTE_SUPPORT_SLUG;

export const PRIVILEGED_REMOTE_ACCESS_SLUG = 'privileged-remote-access' as const;
export type PrivilegedRemoteAccessSlug = typeof PRIVILEGED_REMOTE_ACCESS_SLUG;

export const ADMIN_SLUG = 'admin' as const;
export type AdminSlug = typeof ADMIN_SLUG;

export const AI_COWORKERS_SLUG = 'ai-coworkers' as const;
export type AiCoworkersSlug = typeof AI_COWORKERS_SLUG;

export const PLATFORM_SCOPE = 'email' as const;
export type PlatformScope = typeof PLATFORM_SCOPE;

export const INSIGHTS_SCOPE = 'insights' as const;
export type InsightsScope = typeof INSIGHTS_SCOPE;

export const EPML_SCOPE = 'epml' as const;
export type EpmlScope = typeof EPML_SCOPE;

export const PMC_SCOPE = 'epmwm' as const;
export type PmcScope = typeof PMC_SCOPE;

export const PASSWORD_SAFE_SCOPE = 'psc' as const;
export type PasswordSafeScope = typeof PASSWORD_SAFE_SCOPE;

export const SECRETS_SCOPE = 'secrets' as const;
export type SecretsScope = typeof SECRETS_SCOPE;

export const ENTITLE_SCOPE = 'entitle' as const;
export type EntitleScope = typeof ENTITLE_SCOPE;

export const REMOTE_SUPPORT_SCOPE = 'rs' as const;
export type RemoteSupportScope = typeof REMOTE_SUPPORT_SLUG;

export const PRIVILEGED_REMOTE_ACCESS_SCOPE = 'pra' as const;
export type PrivilegedRemoteAccessScope = typeof PRIVILEGED_REMOTE_ACCESS_SCOPE;

export const ADMIN_SCOPE = 'admin' as const;
export type AdminScope = typeof ADMIN_SCOPE;

// The entitlement token the /api/platform/currentSite API returns for the
// AI Agent Security product; drives whether its menu item is shown.
export const AIDR_SCOPE = 'aidr' as const;
export type AidrScope = typeof AIDR_SCOPE;

/**
 * Supported tenant types represented as a readonly
 * array of constants.
 */
export const TENANT_TYPES = [
  INSIGHTS_SLUG,
  'pmul-c',
  EPML_SLUG,
  PMC_SLUG,
  PASSWORD_SAFE_SLUG,
  ENTITLE_SLUG,
  REMOTE_SUPPORT_SLUG,
  PRIVILEGED_REMOTE_ACCESS_SLUG,
  SECRETS_SLUG,
] as const;

/**
 * Supported tenant types
 */
export type TenantType = (typeof TENANT_TYPES)[number];

export interface OrganizationLifeSpan {
  startDate: string;
  endDate: string;
}

export interface Organization {
  id: string;
  name: string;
  error?: string | null;
  isTenant?: boolean | null;
  slug?: string | null;
  hasOrgAccess?: boolean | null;
  insightsEnabled?: boolean | null;
  lifeSpan?: OrganizationLifeSpan | null;
}

export interface Tenant extends Organization {
  organizationId: string;
  applicationName: TenantType;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export interface TenantResponse {
  tenants: Tenant[];
  error?: string | null;
}

export interface UserInfo {
  givenName: string;
  familyName: string;
  email: string;
  sub: string;
  error?: string | null;
}

export interface UserProfile {
  userInfo: UserInfo;
  organization: Organization;
  tenants: TenantResponse;
  preferredTenant: string;
  annotations?: unknown | null;
}

export interface ProfileState {
  profile: UserProfile;
  activeTenant: Tenant | Organization;
}

export const PRODUCT_SLUGS = [
  PLATFORM_SLUG,
  INSIGHTS_SLUG,
  EPML_SLUG,
  PMC_SLUG,
  PASSWORD_SAFE_SLUG,
  ENTITLE_SLUG,
  SECRETS_SLUG,
  REMOTE_SUPPORT_SLUG,
  PRIVILEGED_REMOTE_ACCESS_SLUG,
  ADMIN_SLUG,
  AI_COWORKERS_SLUG,
] as const;
export type ProductSlug = (typeof PRODUCT_SLUGS)[number];

export const PRODUCT_SCOPES = [
  PLATFORM_SCOPE,
  INSIGHTS_SCOPE,
  EPML_SCOPE,
  PMC_SCOPE,
  PASSWORD_SAFE_SCOPE,
  ENTITLE_SCOPE,
  SECRETS_SCOPE,
  REMOTE_SUPPORT_SCOPE,
  PRIVILEGED_REMOTE_ACCESS_SCOPE,
  ADMIN_SCOPE,
  AIDR_SCOPE,
] as const;
export type ProductScope = (typeof PRODUCT_SCOPES)[number];

export interface ProductMenuItem {
  name: string;
  slug: ProductSlug;
  baseHref: string;
  /**
   * Paths that still resolve to this product but are never linked to — a
   * superseded base path kept matchable so existing bookmarks keep working.
   */
  altBaseHrefs?: string[];
  // INFO(kohearon): This is temporary
  // data to as a stop-gap for linking
  // out to product pages for those products
  // which a user does not have enabled
  productInformationUrl: string;
  helpUrl: string;
  isAdminApp?: boolean;
  scopeName: ProductScope;
  menuSlotName: string;
}

export interface RenderData {
  site: SiteData;
  productMenuItems: ProductMenuItem[];
  currentProduct: ProductMenuItem;
  sites: SiteAccessibility[];
}

export interface SiteAccessibility {
  site_id: string;
  name: string;
  slug: string;
  residency: string;
}

export interface SiteData {
  external_id: string;
  site_id: string;
  name: string;
  residency: string;
  products: SiteProductDetails[];
  slug: string;
  legacy_slug?: string;
}

export interface SiteProductDetails {
  name: string;
}

export interface APIMenuDefinition {
  features: APIFeatureDefinition[];
}
export interface APIFeatureDefinition {
  name?: string;
  id?: string;
  items: APIMenuItem[];
  link?: string;
  tags?: APITagItem[];
  featureFlag?: string;
  /** Optional btux-svg icon name; newer menu APIs may provide it. */
  icon?: string;
  /** When true, the link is not prefixed with the product base URL. Defaults to false. */
  absolutePath?: boolean;
}

export interface APIMenuItem {
  name: string;
  id: string;
  link: string;
  tags?: APITagItem[];
  openExternal?: boolean;
  featureFlag?: string;
  /** When true, the link is not prefixed with the product base URL. Defaults to false. */
  absolutePath?: boolean;
}

export interface APITagItem {
  name: string;
}

export interface SRAMenuResponse {
  id: string;
  title: string;
  featureFlag?: string;
  items: {
    id: string;
    path: string;
  }[];
}

export interface APIInformation {
  apiURL: string;
  baseURL: string;

  // Allow any as we don't always control the API response
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transformer?: (resp: any) => APIMenuDefinition;
}

export interface UserInformation {
  familyName: string;
  givenName: string;
  email: string;
  userId: string;
  externalId: string;
  siteId: string;
}

export interface FeatureFlagsConfig {
  platformClientID: string;
  streamURI: string;
  baseURI: string;
  eventsURI: string;
}

export interface CurrentSiteFeatures {
  platformAiChat: boolean;
}
